/* eslint-disable react/no-unescaped-entities */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../../config/api';
import { services } from '../../services';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const AdminPage: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<any[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingUser, setEditingUser] = useState<any>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState<any>(null);

  // Create account form states
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('admin');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Edit user form states
  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editRole, setEditRole] = useState('admin');
  const [editError, setEditError] = useState('');
  const [editSuccess, setEditSuccess] = useState('');

  const fetchUsers = async () => {
    setUsersLoading(true);
    try {
      const response = await services.get(API_ENDPOINTS.USERS.LIST);
      setUsers(response.data.data || response.data);
    } catch (err: any) {
      console.error('Failed to fetch users:', err);
      if (err.status === 401 || err.data?.message?.includes('Not authorized')) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
        return;
      }
    } finally {
      setUsersLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const openCreateForm = () => {
    setShowCreateForm(true);
    setError('');
    setSuccess('');
  };

  const closeCreateForm = () => {
    setShowCreateForm(false);
    setFirstName('');
    setLastName('');
    setEmail('');
    setPassword('');
    setRole('admin');
    setError('');
    setSuccess('');
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!firstName || !lastName || !email || !password) {
      setError('All fields are required');
      return;
    }

    try {
      await services.post(API_ENDPOINTS.AUTH.REGISTER, {
        firstName,
        lastName,
        email,
        password,
        role,
      });
      setSuccess('Account created successfully!');
      fetchUsers();
      setTimeout(() => {
        closeCreateForm();
      }, 1500);
    } catch (err: any) {
      console.error('Failed to create account:', err);
      if (err.status === 401 || err.data?.message?.includes('Not authorized')) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
        return;
      }
      setError(err.data?.message || 'Failed to create account');
    }
  };

  // Edit user functions
  const handleEditUser = (user: any) => {
    setEditingUser(user);
    setEditFirstName(user.firstName || '');
    setEditLastName(user.lastName || '');
    setEditEmail(user.email || '');
    setEditRole(user.role || 'admin');
    setEditError('');
    setEditSuccess('');
    setShowEditForm(true);
  };

  const closeEditForm = () => {
    setShowEditForm(false);
    setEditingUser(null);
    setEditFirstName('');
    setEditLastName('');
    setEditEmail('');
    setEditRole('admin');
    setEditError('');
    setEditSuccess('');
  };

  const onSubmitEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    setEditError('');
    setEditSuccess('');

    if (!editFirstName || !editLastName || !editEmail) {
      setEditError('All fields are required');
      return;
    }

    try {
      await services.put(API_ENDPOINTS.USERS.UPDATE(editingUser._id || editingUser.id), {
        firstName: editFirstName,
        lastName: editLastName,
        email: editEmail,
        role: editRole,
      });
      setEditSuccess('User updated successfully!');
      fetchUsers();
      setTimeout(() => {
        closeEditForm();
      }, 1500);
    } catch (err: any) {
      console.error('Failed to update user:', err);
      if (err.status === 401 || err.data?.message?.includes('Not authorized')) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
        return;
      }
      setEditError(err.data?.message || 'Failed to update user');
    }
  };

  // Delete user functions
  const handleDeleteUser = (user: any) => {
    setUserToDelete(user);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      await services.delete(API_ENDPOINTS.USERS.DELETE(userToDelete._id || userToDelete.id));
      fetchUsers();
      setShowDeleteConfirm(false);
      setUserToDelete(null);
    } catch (err: any) {
      console.error('Failed to delete user:', err);
      if (err.status === 401 || err.data?.message?.includes('Not authorized')) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
        return;
      }
    }
  };

  const cancelDeleteUser = () => {
    setShowDeleteConfirm(false);
    setUserToDelete(null);
  };

  return (
    <div>
      <div className='flex justify-between items-center mb-8'>
        <h2
          className='text-3xl font-bold font-dm-serif'
          style={{
            color: '#ede8c5',
            textShadow: '0 2px 6px rgba(0, 0, 0, 0.4), 0 0 10px rgba(187, 228, 37, 0.2)',
            letterSpacing: '0.5px',
          }}
        >
          Admin Management
        </h2>
        <Button
          onClick={openCreateForm}
          className='hover:opacity-80 font-work-sans transition-all duration-300 hover:scale-105'
          style={{
            backgroundColor: '#095D52',
            color: '#ede8c5',
            border: '1px solid #095D52',
            boxShadow: '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)',
            backgroundImage:
              'linear-gradient(135deg, rgba(187, 228, 37, 0.1) 0%, rgba(9, 93, 82, 0.8) 100%)',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#bbe425';
            e.currentTarget.style.boxShadow =
              '0 6px 20px rgba(187, 228, 37, 0.5), 0 0 0 1px rgba(187, 228, 37, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#095D52';
            e.currentTarget.style.boxShadow =
              '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)';
          }}
        >
          Create Account
        </Button>
      </div>

      {usersLoading ? (
        <div className='flex items-center justify-center py-12'>
          <div className='text-center'>
            <div
              className='animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4'
              style={{ borderColor: '#095D52' }}
            ></div>
            <p className='text-lg font-work-sans' style={{ color: '#ede8c5' }}>
              Loading admin users...
            </p>
          </div>
        </div>
      ) : (
        <div
          className='rounded-lg overflow-hidden shadow-lg transition-all duration-300'
          style={{
            backgroundColor: '#0B222C',
            border: '2px solid #095D52',
            boxShadow:
              '0 8px 25px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(187, 228, 37, 0.2), inset 0 1px 0 rgba(187, 228, 37, 0.1)',
            backgroundImage:
              'linear-gradient(135deg, rgba(187, 228, 37, 0.03) 0%, rgba(9, 93, 82, 0.05) 50%, rgba(11, 34, 44, 0.98) 100%)',
          }}
        >
          <div className='overflow-x-auto'>
            <table className='min-w-full'>
              <thead
                style={{
                  backgroundColor: '#095D52',
                  backgroundImage:
                    'linear-gradient(135deg, rgba(187, 228, 37, 0.1) 0%, rgba(9, 93, 82, 0.8) 100%)',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                }}
              >
                <tr>
                  <th
                    className='px-6 py-4 text-left text-xs font-bold uppercase tracking-wider font-work-sans'
                    style={{ color: '#ede8c5' }}
                  >
                    Name
                  </th>
                  <th
                    className='px-6 py-4 text-left text-xs font-bold uppercase tracking-wider font-work-sans'
                    style={{ color: '#ede8c5' }}
                  >
                    Email
                  </th>
                  <th
                    className='px-6 py-4 text-left text-xs font-bold uppercase tracking-wider font-work-sans'
                    style={{ color: '#ede8c5' }}
                  >
                    Role
                  </th>
                  <th
                    className='px-6 py-4 text-left text-xs font-bold uppercase tracking-wider font-work-sans'
                    style={{ color: '#ede8c5' }}
                  >
                    Status
                  </th>
                  <th
                    className='px-6 py-4 text-left text-xs font-bold uppercase tracking-wider font-work-sans'
                    style={{ color: '#ede8c5' }}
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody style={{ backgroundColor: '#0B222C' }} className='divide-y divide-gray-700'>
                {users
                  .filter((user: any) => user.role === 'admin')
                  .map((user: any) => (
                    <tr
                      key={user._id || user.id}
                      className='transition-colors'
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#095D52')}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                    >
                      <td className='px-6 py-4 whitespace-nowrap'>
                        <div
                          className='text-sm font-medium font-work-sans'
                          style={{ color: '#ede8c5' }}
                        >
                          {String(user.firstName || '')} {String(user.lastName || '')}
                        </div>
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap'>
                        <div className='text-sm font-work-sans' style={{ color: '#ede8c5' }}>
                          {String(user.email || '')}
                        </div>
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap'>
                        <span
                          className='inline-flex px-3 py-1 text-xs font-semibold rounded-full font-work-sans'
                          style={{ backgroundColor: '#095D52', color: '#ede8c5' }}
                        >
                          {String(user.role || '')}
                        </span>
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap'>
                        <span
                          className='inline-flex px-3 py-1 text-xs font-semibold rounded-full font-work-sans'
                          style={{
                            backgroundColor: user.isActive ? '#10B981' : '#EF4444',
                            color: '#ede8c5',
                          }}
                        >
                          {user.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap'>
                        <div className='flex space-x-2'>
                          <button
                            onClick={() => handleEditUser(user)}
                            className='p-2 rounded-lg transition-all duration-200 hover:scale-105 focus:outline-none'
                            style={{
                              backgroundColor: '#0B222C',
                              border: '1px solid #095D52',
                              color: '#ede8c5',
                              boxShadow:
                                '0 2px 8px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(187, 228, 37, 0.1)',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#095D52';
                              e.currentTarget.style.borderColor = '#bbe425';
                              e.currentTarget.style.boxShadow =
                                '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = '#0B222C';
                              e.currentTarget.style.borderColor = '#095D52';
                              e.currentTarget.style.boxShadow =
                                '0 2px 8px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(187, 228, 37, 0.1)';
                            }}
                            title='Edit User'
                          >
                            <svg
                              className='w-4 h-4'
                              fill='none'
                              stroke='currentColor'
                              viewBox='0 0 24 24'
                            >
                              <path
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                strokeWidth={2}
                                d='M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z'
                              />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDeleteUser(user)}
                            className='p-2 rounded-lg transition-all duration-200 hover:scale-105 focus:outline-none'
                            style={{
                              backgroundColor: '#0B222C',
                              border: '1px solid #095D52',
                              color: '#ede8c5',
                              boxShadow:
                                '0 2px 8px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(187, 228, 37, 0.1)',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#EF4444';
                              e.currentTarget.style.borderColor = '#DC2626';
                              e.currentTarget.style.boxShadow =
                                '0 4px 15px rgba(239, 68, 68, 0.4), 0 0 0 1px rgba(220, 38, 38, 0.3)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = '#0B222C';
                              e.currentTarget.style.borderColor = '#095D52';
                              e.currentTarget.style.boxShadow =
                                '0 2px 8px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(187, 228, 37, 0.1)';
                            }}
                            title='Delete User'
                          >
                            <svg
                              className='w-4 h-4'
                              fill='none'
                              stroke='currentColor'
                              viewBox='0 0 24 24'
                            >
                              <path
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                strokeWidth={2}
                                d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16'
                              />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create Account Modal */}
      {showCreateForm && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
          <div
            className='rounded-xl border max-w-md w-full mx-4 shadow-lg'
            style={{
              backgroundColor: '#0B222C',
              borderColor: '#095D52',
              boxShadow:
                '0 8px 25px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(187, 228, 37, 0.2), inset 0 1px 0 rgba(187, 228, 37, 0.1)',
              backgroundImage:
                'linear-gradient(135deg, rgba(187, 228, 37, 0.03) 0%, rgba(9, 93, 82, 0.05) 50%, rgba(11, 34, 44, 0.98) 100%)',
            }}
          >
            <div className='p-6'>
              <div className='flex justify-between items-center mb-6'>
                <h2
                  className='text-2xl font-bold font-dm-serif'
                  style={{
                    color: '#ede8c5',
                    textShadow: '0 2px 8px rgba(0, 0, 0, 0.5), 0 0 15px rgba(187, 228, 37, 0.3)',
                    letterSpacing: '0.8px',
                  }}
                >
                  Create New Account
                </h2>
                <button
                  onClick={closeCreateForm}
                  className='text-2xl transition-all duration-300 hover:scale-110'
                  style={{ color: '#ede8c5' }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = '#bbe425')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = '#ede8c5')}
                >
                  ×
                </button>
              </div>

              <form onSubmit={onSubmit} className='space-y-4'>
                <div>
                  <label
                    className='block text-sm font-medium font-work-sans mb-2'
                    style={{ color: '#ede8c5' }}
                  >
                    First Name
                  </label>
                  <Input
                    type='text'
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className='w-full font-work-sans'
                    placeholder='Enter first name'
                  />
                </div>

                <div>
                  <label
                    className='block text-sm font-medium font-work-sans mb-2'
                    style={{ color: '#ede8c5' }}
                  >
                    Last Name
                  </label>
                  <Input
                    type='text'
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className='w-full font-work-sans'
                    placeholder='Enter last name'
                  />
                </div>

                <div>
                  <label
                    className='block text-sm font-medium font-work-sans mb-2'
                    style={{ color: '#ede8c5' }}
                  >
                    Email
                  </label>
                  <Input
                    type='email'
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className='w-full font-work-sans'
                    placeholder='Enter email address'
                  />
                </div>

                <div>
                  <label
                    className='block text-sm font-medium font-work-sans mb-2'
                    style={{ color: '#ede8c5' }}
                  >
                    Password
                  </label>
                  <Input
                    type='password'
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className='w-full font-work-sans'
                    placeholder='Enter password'
                  />
                </div>

                <div>
                  <label
                    className='block text-sm font-medium font-work-sans mb-2'
                    style={{ color: '#ede8c5' }}
                  >
                    Role
                  </label>
                  <select
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    className='w-full px-3 py-2 rounded-md focus:outline-none focus:ring-2 font-work-sans'
                    style={{
                      backgroundColor: '#0B222C',
                      border: '1px solid #095D52',
                      color: '#ede8c5',
                    }}
                  >
                    <option value='user'>User</option>
                    <option value='admin'>Admin</option>
                  </select>
                </div>

                {error && (
                  <div
                    className='p-3 rounded-lg font-work-sans'
                    style={{
                      backgroundColor: '#FEF2F2',
                      border: '1px solid #FECACA',
                      color: '#DC2626',
                    }}
                  >
                    {error}
                  </div>
                )}

                {success && (
                  <div
                    className='p-3 rounded-lg font-work-sans'
                    style={{
                      backgroundColor: '#F0FDF4',
                      border: '1px solid #BBF7D0',
                      color: '#16A34A',
                    }}
                  >
                    {success}
                  </div>
                )}

                <div className='flex space-x-3 pt-4'>
                  <Button
                    type='button'
                    onClick={closeCreateForm}
                    className='flex-1 font-work-sans transition-all duration-300 hover:scale-105'
                    style={{
                      backgroundColor: '#095D52',
                      color: '#ede8c5',
                      border: '1px solid #095D52',
                      boxShadow:
                        '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#bbe425';
                      e.currentTarget.style.boxShadow =
                        '0 6px 20px rgba(187, 228, 37, 0.5), 0 0 0 1px rgba(187, 228, 37, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#095D52';
                      e.currentTarget.style.boxShadow =
                        '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)';
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type='submit'
                    className='flex-1 font-work-sans transition-all duration-300 hover:scale-105'
                    style={{
                      backgroundColor: '#095D52',
                      color: '#ede8c5',
                      border: '1px solid #095D52',
                      boxShadow:
                        '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#bbe425';
                      e.currentTarget.style.boxShadow =
                        '0 6px 20px rgba(187, 228, 37, 0.5), 0 0 0 1px rgba(187, 228, 37, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#095D52';
                      e.currentTarget.style.boxShadow =
                        '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)';
                    }}
                  >
                    Create Account
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditForm && editingUser && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
          <div
            className='rounded-xl border max-w-md w-full mx-4 shadow-lg'
            style={{
              backgroundColor: '#0B222C',
              borderColor: '#095D52',
              boxShadow:
                '0 8px 25px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(187, 228, 37, 0.2), inset 0 1px 0 rgba(187, 228, 37, 0.1)',
              backgroundImage:
                'linear-gradient(135deg, rgba(187, 228, 37, 0.03) 0%, rgba(9, 93, 82, 0.05) 50%, rgba(11, 34, 44, 0.98) 100%)',
            }}
          >
            <div className='p-6'>
              <div className='flex justify-between items-center mb-6'>
                <h2
                  className='text-2xl font-bold font-dm-serif'
                  style={{
                    color: '#ede8c5',
                    textShadow: '0 2px 8px rgba(0, 0, 0, 0.5), 0 0 15px rgba(187, 228, 37, 0.3)',
                    letterSpacing: '0.8px',
                  }}
                >
                  Edit User
                </h2>
                <button
                  onClick={closeEditForm}
                  className='text-2xl transition-all duration-300 hover:scale-110'
                  style={{ color: '#ede8c5' }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = '#bbe425')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = '#ede8c5')}
                >
                  ×
                </button>
              </div>

              <form onSubmit={onSubmitEdit} className='space-y-4'>
                <div>
                  <label
                    className='block text-sm font-medium font-work-sans mb-2'
                    style={{ color: '#ede8c5' }}
                  >
                    First Name
                  </label>
                  <Input
                    type='text'
                    value={editFirstName}
                    onChange={(e) => setEditFirstName(e.target.value)}
                    className='w-full font-work-sans'
                    placeholder='Enter first name'
                  />
                </div>

                <div>
                  <label
                    className='block text-sm font-medium font-work-sans mb-2'
                    style={{ color: '#ede8c5' }}
                  >
                    Last Name
                  </label>
                  <Input
                    type='text'
                    value={editLastName}
                    onChange={(e) => setEditLastName(e.target.value)}
                    className='w-full font-work-sans'
                    placeholder='Enter last name'
                  />
                </div>

                <div>
                  <label
                    className='block text-sm font-medium font-work-sans mb-2'
                    style={{ color: '#ede8c5' }}
                  >
                    Email
                  </label>
                  <Input
                    type='email'
                    value={editEmail}
                    onChange={(e) => setEditEmail(e.target.value)}
                    className='w-full font-work-sans'
                    placeholder='Enter email address'
                  />
                </div>

                <div>
                  <label
                    className='block text-sm font-medium font-work-sans mb-2'
                    style={{ color: '#ede8c5' }}
                  >
                    Role
                  </label>
                  <select
                    value={editRole}
                    onChange={(e) => setEditRole(e.target.value)}
                    className='w-full px-3 py-2 rounded-md focus:outline-none focus:ring-2 font-work-sans'
                    style={{
                      backgroundColor: '#0B222C',
                      border: '1px solid #095D52',
                      color: '#ede8c5',
                    }}
                  >
                    <option value='user'>User</option>
                    <option value='admin'>Admin</option>
                  </select>
                </div>

                {editError && (
                  <div
                    className='p-3 rounded-lg font-work-sans'
                    style={{
                      backgroundColor: '#FEF2F2',
                      border: '1px solid #FECACA',
                      color: '#DC2626',
                    }}
                  >
                    {editError}
                  </div>
                )}

                {editSuccess && (
                  <div
                    className='p-3 rounded-lg font-work-sans'
                    style={{
                      backgroundColor: '#F0FDF4',
                      border: '1px solid #BBF7D0',
                      color: '#16A34A',
                    }}
                  >
                    {editSuccess}
                  </div>
                )}

                <div className='flex space-x-3 pt-4'>
                  <Button
                    type='button'
                    onClick={closeEditForm}
                    className='flex-1 font-work-sans transition-all duration-300 hover:scale-105'
                    style={{
                      backgroundColor: '#095D52',
                      color: '#ede8c5',
                      border: '1px solid #095D52',
                      boxShadow:
                        '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#bbe425';
                      e.currentTarget.style.boxShadow =
                        '0 6px 20px rgba(187, 228, 37, 0.5), 0 0 0 1px rgba(187, 228, 37, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#095D52';
                      e.currentTarget.style.boxShadow =
                        '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)';
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type='submit'
                    className='flex-1 font-work-sans transition-all duration-300 hover:scale-105'
                    style={{
                      backgroundColor: '#095D52',
                      color: '#ede8c5',
                      border: '1px solid #095D52',
                      boxShadow:
                        '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#bbe425';
                      e.currentTarget.style.boxShadow =
                        '0 6px 20px rgba(187, 228, 37, 0.5), 0 0 0 1px rgba(187, 228, 37, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#095D52';
                      e.currentTarget.style.boxShadow =
                        '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)';
                    }}
                  >
                    Update User
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete User Confirmation Modal */}
      {showDeleteConfirm && userToDelete && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
          <div
            className='rounded-xl border max-w-md w-full mx-4 shadow-lg'
            style={{
              backgroundColor: '#0B222C',
              borderColor: '#095D52',
              boxShadow:
                '0 8px 25px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(187, 228, 37, 0.2), inset 0 1px 0 rgba(187, 228, 37, 0.1)',
              backgroundImage:
                'linear-gradient(135deg, rgba(187, 228, 37, 0.03) 0%, rgba(9, 93, 82, 0.05) 50%, rgba(11, 34, 44, 0.98) 100%)',
            }}
          >
            <div className='p-6'>
              <div className='flex items-center mb-4'>
                <div className='flex-shrink-0'>
                  <svg
                    className='w-8 h-8'
                    style={{ color: '#EF4444' }}
                    fill='currentColor'
                    viewBox='0 0 20 20'
                  >
                    <path
                      fillRule='evenodd'
                      d='M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z'
                      clipRule='evenodd'
                    />
                  </svg>
                </div>
                <div className='ml-3'>
                  <h3
                    className='text-lg font-medium font-dm-serif'
                    style={{
                      color: '#ede8c5',
                      textShadow: '0 2px 6px rgba(0, 0, 0, 0.4), 0 0 10px rgba(187, 228, 37, 0.2)',
                      letterSpacing: '0.5px',
                    }}
                  >
                    Delete User
                  </h3>
                </div>
              </div>

              <div className='mb-6'>
                <p className='text-sm font-work-sans' style={{ color: '#ede8c5' }}>
                  Are you sure you want to delete{' '}
                  <span className='font-semibold' style={{ color: '#ede8c5' }}>
                    "{String(userToDelete.firstName || '')} {String(userToDelete.lastName || '')}"
                  </span>
                  ?
                </p>
              </div>

              <div className='flex space-x-3'>
                <button
                  onClick={cancelDeleteUser}
                  className='flex-1 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 font-work-sans hover:scale-105'
                  style={{
                    backgroundColor: '#095D52',
                    color: '#ede8c5',
                    border: '1px solid #095D52',
                    boxShadow: '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#bbe425';
                    e.currentTarget.style.boxShadow =
                      '0 6px 20px rgba(187, 228, 37, 0.5), 0 0 0 1px rgba(187, 228, 37, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#095D52';
                    e.currentTarget.style.boxShadow =
                      '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)';
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteUser}
                  className='flex-1 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 font-work-sans hover:scale-105'
                  style={{
                    backgroundColor: '#EF4444',
                    color: '#ede8c5',
                    boxShadow:
                      '0 4px 15px rgba(239, 68, 68, 0.4), 0 0 0 1px rgba(220, 38, 38, 0.3)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#DC2626';
                    e.currentTarget.style.boxShadow =
                      '0 6px 20px rgba(220, 38, 38, 0.5), 0 0 0 1px rgba(185, 28, 28, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#EF4444';
                    e.currentTarget.style.boxShadow =
                      '0 4px 15px rgba(239, 68, 68, 0.4), 0 0 0 1px rgba(220, 38, 38, 0.3)';
                  }}
                >
                  Delete User
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
